import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CustomValidatorErrorEnum } from '@shared/enums';
import { RegexTypeEnum } from '@shared/enums/regex.enum';
import {
    createStringUnicityDictionary,
    manageFrontEndNotUniqueError,
} from 'src/app/features/orders/order-setup/_core/helpers/order-setup.helper';
import {
    CustomPrimerFormGroup,
    ReactionDetailsReactionForm,
    ShippedPrimerFormGroup,
} from 'src/app/features/orders/order-setup/_core/interfaces';
import { NumberOfSamplesFormGroup } from 'src/app/features/orders/order-setup/_core/interfaces/number-of-reactions.interface';

export class CustomValidators {
    static emailValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const emailRegex = new RegExp(RegexTypeEnum.EMAIL_PATTERN);

            const isValid = emailRegex.test(control.value);

            return isValid ? null : { [CustomValidatorErrorEnum.INVALID_EMAIL]: true };
        };
    }

    static emptyInputValidator(inputValue: FormControl): ValidationErrors | null {
        if (!inputValue.value) {
            return null;
        }
        return inputValue!.value.trim() !== '' ? null : { [CustomValidatorErrorEnum.EMPTY_INPUT]: true };
    }

    static maxArrayLength(maxValue: number) {
        return (inputControl: AbstractControl) => {
            if (!inputControl.value) {
                return null;
            }

            return inputControl.value.length <= maxValue ? null : { [CustomValidatorErrorEnum.MAX_ARRAY_SIZE]: true };
        };
    }

    static emptyPaymentValueValidator(inputValue: FormControl): ValidationErrors | null {
        if (!inputValue.value) {
            return null;
        }
        if (inputValue.value.value) {
            return null;
        }
        return inputValue.value.trim() !== '' ? null : { [CustomValidatorErrorEnum.EMPTY_INPUT]: true };
    }

    static emptyStringOrObjectValidator(inputValue: FormControl<string | { value: string }>): ValidationErrors | null {
        if (typeof inputValue.value === 'string') {
            return inputValue!.value?.trim() !== '' ? null : { [CustomValidatorErrorEnum.EMPTY_INPUT]: true };
        } else {
            return inputValue!.value?.value?.trim() !== '' ? null : { [CustomValidatorErrorEnum.EMPTY_INPUT]: true };
        }
    }

    static uniqueSequences(control: AbstractControl): ValidatorFn | null {
        const formArray = control as FormArray<FormGroup<CustomPrimerFormGroup>>;

        const sequenceIndices = createStringUnicityDictionary(formArray, 'sequence');
        manageFrontEndNotUniqueError(sequenceIndices, formArray, 'sequence');

        return null;
    }

    static uniqueBarcodes(control: AbstractControl): ValidatorFn | null {
        const formArray = control as FormArray<FormGroup<ShippedPrimerFormGroup | ReactionDetailsReactionForm>>;

        const barcodeIndices = createStringUnicityDictionary(formArray, 'barcode');
        manageFrontEndNotUniqueError(barcodeIndices, formArray, 'barcode');

        return null;
    }

    static uniqueSampleNames(control: AbstractControl): ValidatorFn | null {
        const formArray = control as FormArray<FormGroup<ReactionDetailsReactionForm>>;

        const sampleNameIndices = createStringUnicityDictionary(formArray, 'sampleName');
        manageFrontEndNotUniqueError(sampleNameIndices, formArray, 'sampleName');

        return null;
    }

    static uniquePrimerNames(control: AbstractControl): ValidatorFn | null {
        const form: FormGroup<NumberOfSamplesFormGroup> = control as any;

        const nameIndices: { [name: string]: { index: number; formOrigin: 'custom' | 'shipped' }[] } = {};

        const availableCustomPrimersControls = form.controls.customPrimers.controls.filter(control => control.enabled);

        for (let index = 0; index < availableCustomPrimersControls.length; index++) {
            const primerNameValue = form.controls.customPrimers.controls[index].controls.name.value?.trim();
            if (primerNameValue) {
                if (!nameIndices[primerNameValue]) {
                    nameIndices[primerNameValue] = [];
                }
                nameIndices[primerNameValue].push({ index: index, formOrigin: 'custom' });
            }
        }

        const availableShippedPrimersControls = form.controls.shippedPrimers.controls.filter(
            control => control.enabled
        );

        for (let index = 0; index < availableShippedPrimersControls.length; index++) {
            const primerNameValue = form.controls.shippedPrimers.controls[index].controls.name.value?.trim();
            if (primerNameValue) {
                if (!nameIndices[primerNameValue]) {
                    nameIndices[primerNameValue] = [];
                }
                nameIndices[primerNameValue].push({ index: index, formOrigin: 'shipped' });
            }
        }

        Object.values(nameIndices).forEach(nameIndicesArray => {
            if (nameIndicesArray.length > 1) {
                nameIndicesArray.forEach(nameObject => {
                    if (nameObject.formOrigin === 'custom') {
                        form.controls.customPrimers.controls[nameObject.index].controls.name.setErrors({
                            [CustomValidatorErrorEnum.FRONTEND_NOT_UNIQUE]: true,
                        });
                    } else {
                        form.controls.shippedPrimers.controls[nameObject.index].controls.name.setErrors({
                            [CustomValidatorErrorEnum.FRONTEND_NOT_UNIQUE]: true,
                        });
                    }
                });
            } else {
                let control: FormControl<string | null>;

                if (nameIndicesArray[0].formOrigin === 'custom') {
                    control = form.controls.customPrimers.controls[nameIndicesArray[0].index].controls.name;
                } else {
                    control = form.controls.shippedPrimers.controls[nameIndicesArray[0].index].controls.name;
                }

                if (control.hasError(CustomValidatorErrorEnum.FRONTEND_NOT_UNIQUE) && control.errors) {
                    delete control.errors[CustomValidatorErrorEnum.FRONTEND_NOT_UNIQUE];
                    control.updateValueAndValidity();
                }
            }
        });

        return null;
    }
}
